import type { ReactNode } from "react";
import {
  Pressable,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { semanticColors, typography } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type SoftSecondaryButtonProps = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  disabled?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  testID?: string;
  /** Set when used inside a row with sibling buttons. Omit for standalone (full-width). */
  flex?: number;
  style?: StyleProp<ViewStyle>;
};

export function SoftSecondaryButton({
  label,
  onPress,
  action,
  accessibilityLabel,
  disabled = false,
  leading,
  trailing,
  testID,
  flex,
  style,
}: SoftSecondaryButtonProps) {
  const capturePress = usePressCapture("SoftSecondaryButton");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress();
      }}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled }}
      testID={testID}
      style={[
        {
          ...(flex !== undefined && { flex }),
          paddingVertical: 14,
          borderRadius: 999,
          backgroundColor: semanticColors.overlay.pressed,
          alignItems: "center",
          justifyContent: "center",
          opacity: disabled ? 0.55 : 1,
        },
        style,
      ]}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        {leading}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: "500",
            fontFamily: typography.families.sans,
            color: semanticColors.text.secondary,
          }}
        >
          {label}
        </Text>
        {trailing}
      </View>
    </Pressable>
  );
}
